/**
 * The inventory tree's client model (release 2.1 item 14): only EXPANDED
 * nodes are materialised, flattened into the rows the virtual list draws.
 * Pure functions, unit-tested; the page owns the state and the fetching.
 */

import type { TreeItem } from '@/lib/fleet-api';

export interface ChildState {
  items: TreeItem[];
  nextCursor: string | null;
  total: number;
  loading: boolean;
  error: string | null;
}

export type Row =
  | { kind: 'item'; key: string; depth: number; item: TreeItem; expanded: boolean }
  | { kind: 'more'; key: string; depth: number; parentId: string; shown: number; total: number; loading: boolean }
  | { kind: 'loading'; key: string; depth: number; parentId: string }
  | { kind: 'error'; key: string; depth: number; parentId: string; message: string }
  | { kind: 'empty'; key: string; depth: number; parentId: string };

/**
 * Flatten the expanded part of the tree, depth first, in server order. A
 * node's children appear only when it is expanded AND its first page has been
 * requested; a "load more" row follows a partial page.
 */
export function flatten(
  rootId: string,
  children: ReadonlyMap<string, ChildState>,
  expanded: ReadonlySet<string>,
): Row[] {
  const out: Row[] = [];
  const walk = (parentId: string, depth: number): void => {
    const state = children.get(parentId);
    if (!state) return;
    if (state.error && state.items.length === 0) {
      out.push({ kind: 'error', key: `${parentId}#error`, depth, parentId, message: state.error });
      return;
    }
    if (state.loading && state.items.length === 0) {
      out.push({ kind: 'loading', key: `${parentId}#loading`, depth, parentId });
      return;
    }
    if (state.items.length === 0) {
      out.push({ kind: 'empty', key: `${parentId}#empty`, depth, parentId });
      return;
    }
    for (const item of state.items) {
      const isOpen = expanded.has(item.id);
      out.push({ kind: 'item', key: item.id, depth, item, expanded: isOpen });
      if (isOpen) walk(item.id, depth + 1);
    }
    if (state.nextCursor) {
      out.push({
        kind: 'more',
        key: `${parentId}#more`,
        depth,
        parentId,
        shown: state.items.length,
        total: state.total,
        loading: state.loading,
      });
    }
  };
  walk(rootId, 0);
  return out;
}

/** Append a fetched page to a node's children (or replace them for a first page). */
export function withPage(
  prev: ChildState | undefined,
  page: { items: TreeItem[]; nextCursor: string | null; total: number },
  append: boolean,
): ChildState {
  const items = append && prev ? [...prev.items, ...page.items.filter((i) => !prev.items.some((p) => p.id === i.id))] : page.items;
  return { items, nextCursor: page.nextCursor, total: page.total, loading: false, error: null };
}
